import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button, Input, Card } from '../common';

export function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitExpiry, setRateLimitExpiry] = useState<number | null>(null);
  const { login, isLoading, error, clearError} = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for security reason in URL and prevent redirect loop
  useEffect(() => {
    const reason = searchParams.get('reason');
    if (reason === 'security') {
      setIsRateLimited(true);
      setRateLimitExpiry(Date.now() + 3 * 60 * 1000); // 3 minutes
      // Clear the URL parameter to prevent refresh loop
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate]);

  // Check rate limit expiry
  useEffect(() => {
    if (rateLimitExpiry && Date.now() < rateLimitExpiry) {
      const timer = setInterval(() => {
        if (Date.now() >= rateLimitExpiry) {
          setIsRateLimited(false);
          setRateLimitExpiry(null);
          setFailedAttempts(0);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [rateLimitExpiry]);

  const getRemainingTime = () => {
    if (!rateLimitExpiry) return '';
    const remaining = Math.ceil((rateLimitExpiry - Date.now()) / 1000);
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isRateLimited) {
      return; // Don't allow login if rate limited
    }
    
    clearError();
    
    try {
      await login(email, password, rememberMe);
      // Reset failed attempts on success
      setFailedAttempts(0);
      navigate('/app');
    } catch (err: any) {
      // Increment failed attempts
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);
      
      // Check if rate limited by backend
      if (err.response?.status === 429 || err.response?.data?.message?.includes('rate limit')) {
        setIsRateLimited(true);
        setRateLimitExpiry(Date.now() + 3 * 60 * 1000); // 3 minutes
      }
    }
  };

  return (
    <div className="w-full max-w-md">
      <Card variant="elevated">
        <h1 className="text-3xl font-bold text-white mb-6 text-center">
          Welcome Back
        </h1>
        
        {/* Rate Limit Warning */}
        {isRateLimited && (
          <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-500 px-4 py-3 rounded mb-4">
            <p className="font-semibold">⚠️ Too Many Failed Attempts</p>
            <p className="text-sm mt-1">
              For security reasons, login has been temporarily disabled.
            </p>
            <p className="text-sm mt-1">
              Please wait: <span className="font-mono font-bold">{getRemainingTime()}</span>
            </p>
          </div>
        )}
        
        {/* Failed Attempts Warning */}
        {failedAttempts >= 2 && !isRateLimited && (
          <div className="bg-orange-500/10 border border-orange-500 text-orange-500 px-4 py-3 rounded mb-4">
            <p className="text-sm">
              ⚠️ Warning: {3 - failedAttempts} attempt(s) remaining before temporary lockout
            </p>
          </div>
        )}
        
        {/* Error Message */}
        {error && !isRateLimited && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            disabled={isLoading || isRateLimited}
            fullWidth
          />
          
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            disabled={isLoading || isRateLimited}
            fullWidth
          />
          
          {/* Remember Me Checkbox */}
          <div className="flex items-center">
            <input
              id="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading || isRateLimited}
              className="h-4 w-4 text-primary-500 bg-dark-700 border-gray-600 rounded focus:ring-primary-500 focus:ring-2"
            />
            <label htmlFor="remember-me" className="ml-2 text-sm text-gray-300">
              Remember me for 30 days
            </label>
          </div>
          
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={isRateLimited}
            fullWidth
            size="lg"
          >
            {isRateLimited ? `Wait ${getRemainingTime()}` : isLoading ? 'Logging in...' : 'Login'}
          </Button>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-gray-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary-500 hover:text-primary-400">
              Sign up
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
}
